import { useCallback, useEffect, useState } from 'react';
import { RecordContextProvider, Title, useCreatePath } from 'react-admin';
import { Link, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import { SectionCard, Row } from '../../shared/Panel';
import { wallClockDateTime } from '../../shared/clientTime';
import { dateTimeNoSeconds } from '../../shared/dateTime';
import { FEATURE_TMS_EXPORT_ONE_STEP } from '../../shared/status';
import { FeaturesLoading, useFeatureState } from '../featureFlags/FeatureGate';
import { useHasPermission } from '../PermissionGate';
import { ViaWritePanel } from '../../order/ViaWritePanel';
import { fetchControlWindow, toErrorMessage, type IntakeControlWindow as Window } from './intakeApi';
import { FEATURE_INTAKE_CONTROL_WINDOW, useIntakeSide } from './intakeSides';
import { IntakeFlowCard, useIntakeFlow } from './IntakeFlowCard';
import { PreOrderContractorSection } from '../../pre-order/PreOrderContractorSection';
import { FuelSurchargeSection } from '../fuelSurcharges/FuelSurchargeSection';
import { QuestionMarker, fieldLabel, useRequiredFields } from '../../shared/requiredFields';
import { IntakeDecisionsPanel } from './IntakeDecisionsPanel';
import { PreOrderAttachments } from '../../pre-order/PreOrderAttachments';
import {
  ShowInSourceLink,
  SourcePreviewProvider,
  SourcePreviewToggle,
  SourceSplit,
  useSourcePreview,
} from '../../pre-order/SourcePreview';
import { sourceLinkFor } from '../../pre-order/sourcePreviewRules';

/**
 * Okno kontroli prezlecenia (makieta back office v2, fala B, B1:
 * „Okno zlecenia · perspektywa Administracji”; przełącznik
 * `intakeControlWindow`, S-21.10-02; ścieżki ADM-06, ADM-08, ADM-02).
 *
 * W jednym miejscu to, co już sprawdzamy przed zapisem do VIA: pola, bez
 * których importer nie przyjmie pliku (walidator — osobno plik zlecenia
 * i plik czynności wózkowej), rekomendowana stawka VAT, podobne zlecenia,
 * podgląd plików, stan zapisu i „Jest w TMS”, kopia prezlecenia i — z obiegiem
 * — etap i wątek. Zatwierdzenie, plik i ponowienie — ten sam panel co na
 * karcie zlecenia, tylko dla Administracji.
 *
 * Odstępstwo od makiety (zasada zapisu do TMS): nie ma „Zapisz w TMS”
 * z natychmiastowym numerem i kodem błędu — importer nie odpowiada, numer
 * nadaje TMS, a wynik przychodzi importem („Jest w TMS”).
 *
 * Kontrahent z kartoteki (21.10.2026, `preOrderContractor`, S-21.10-10):
 * wybór obok podpowiedzi VAT — VAT liczy się z wybranego kontrahenta przed
 * połączeniem z TMS, a po połączeniu z kontrahenta z TMS (źródło pod stawką).
 *
 * Pliki od klienta (26.10.2026, `preOrderAttachments`, S-26.10-13): ta sama
 * sekcja co na karcie prezlecenia — Administracja sprawdza dane z dokumentem.
 *
 * Podgląd źródła (29.10.2026, `sourcePreview`, S-29.10-01): „Pokaż źródło”
 * otwiera mail i załączniki po lewej, obok okna — Administracja sprawdza dane
 * z tym, co przysłał klient, bez przechodzenia na kartę. Przy otwartym
 * źródle sekcje okna stoją w jednej kolumnie.
 *
 * Korekta paliwowa (integracja fali B, 21.10.2026; przełącznik
 * `fuelSurcharges`, S-21.10-05): ta sama sekcja co na karcie prezlecenia,
 * pod podpowiedzią VAT — tylko informacja, bez wpływu na marżę (decyzja D8).
 * Po zmianie kontrahenta w oknie wczytuje się ponownie.
 */

export const FEATURE_OFF_NOTE =
  'Okno kontroli jest wyłączone w wersji aplikacji, z której korzystasz.';
export const NO_PERMISSION_NOTE = 'Okno kontroli jest dla przyjęcia zleceń (CS i Administracja).';

export const IntakeControlWindow = () => {
  const on = useFeatureState(FEATURE_INTAKE_CONTROL_WINDOW);
  const allowed = useHasPermission('ORDER_INTAKE');
  const { id } = useParams<{ id: string }>();
  if (on === undefined) return <FeaturesLoading />;
  if (!on || !allowed || !id) {
    return (
      <Box sx={{ p: 2 }}>
        <Title title="Okno kontroli" />
        <Alert severity="info">{on ? NO_PERMISSION_NOTE : FEATURE_OFF_NOTE}</Alert>
      </Box>
    );
  }
  return (
    <SourcePreviewProvider preOrderId={Number(id)}>
      <ControlScreen id={id} />
    </SourcePreviewProvider>
  );
};

const VatSection = ({
  vat,
  contractor,
  withDecisions,
}: {
  vat: Window['vat'];
  contractor: Window['contractor'];
  /** Decyzja o VAT na liście „Do zrobienia” (S-26.10-06) — kontrakt stoi przy „Potwierdź”. */
  withDecisions: boolean;
}) => (
  <SectionCard title="Rekomendowana stawka VAT">
    <Stack spacing={0.5} data-testid="control-vat" data-rate={vat.rate ?? ''}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        {/* Kontrakt `data-testid` paczki (S-22.10-02): decyzja VAT — u nas rekomendacja, bez „potwierdź”. */}
        <Chip
          size="small"
          color={vat.rate ? (vat.certain ? 'success' : 'warning') : 'default'}
          label={vat.rate ?? 'brak danych'}
          data-testid={withDecisions ? 'control-vat-rate' : 'adm-zlecenie-decyzja-vat'}
        />
        {!vat.certain && vat.rate ? (
          <Typography variant="caption" color="text.secondary">
            rekomendacja niepewna
          </Typography>
        ) : null}
      </Stack>
      <Typography variant="body2">{vat.reason}</Typography>
      {vat.missing.length > 0 ? (
        <Typography variant="caption" color="text.secondary">
          Brakuje: {vat.missing.join(', ')}
        </Typography>
      ) : null}
      {contractor?.effectiveLabel ? (
        <Typography variant="caption" color="text.secondary" data-testid="control-vat-source">
          Kontrahent: {contractor.effectiveLabel} — {contractor.sourceLabel}
        </Typography>
      ) : null}
      <Typography variant="caption" color="text.secondary">
        {withDecisions
          ? 'Stawkę wpisuje Administracja w TMS — to podpowiedź, nie zapis; decyzję o stawce zapisuje lista „Do zrobienia”.'
          : 'Stawkę wpisuje Administracja w TMS — to podpowiedź, nie zapis.'}
      </Typography>
    </Stack>
  </SectionCard>
);

const BlockersSection = ({ w }: { w: Window }) => (
  <SectionCard title="Pola blokujące zapis (walidator importera)">
    <Stack spacing={1} data-testid="control-blockers" data-count={w.blockers.length}>
      {!w.order ? (
        <Alert severity="info">
          Prezlecenie nie jest jeszcze zaakceptowane — plik dla VIA powstaje ze zlecenia z naszym numerem.
          {w.preOrder.missingFields.length > 0
            ? ` Do akceptacji brakuje ${w.preOrder.missingFields.length} pól.`
            : ''}
        </Alert>
      ) : w.blockers.length === 0 ? (
        <Alert severity="success">Komplet pól wymaganych przez importer VIA.</Alert>
      ) : (
        <Alert severity="warning">
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {w.blockers.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </Box>
        </Alert>
      )}
      <Typography variant="caption" color="text.secondary">
        Lista pól obowiązkowych to na razie pola walidatora importera — wartość robocza do potwierdzenia przez
        Marathon.
      </Typography>
    </Stack>
  </SectionCard>
);

const FilesSection = ({ w }: { w: Window }) => {
  const files = w.write?.files ?? [];
  if (files.length === 0) return null;
  return (
    <SectionCard title={`Pliki dla VIA (${files.length})`}>
      <Stack spacing={1} data-testid="control-files">
        {files.length > 1 ? (
          <Typography variant="body2" data-testid="control-files-split">
            Jedno zlecenie, {files.length} rekordy w TMS: transport i czynność wózkowa — wózek osobnym plikiem,
            numery obu rekordów nadaje TMS.
          </Typography>
        ) : null}
        {files.map((f) => (
          <Box key={f.kind} data-testid={`control-file-${f.kind}`}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }} useFlexGap>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {f.label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {f.fileName}
              </Typography>
              <Chip
                size="small"
                color={f.errors.length === 0 ? 'success' : 'warning'}
                label={f.errors.length === 0 ? 'komplet pól' : `braki: ${f.errors.length}`}
              />
            </Stack>
            <Box
              component="details"
              sx={{ mt: 0.5, '& pre': { m: 0, maxHeight: 240, overflow: 'auto', fontSize: 11 } }}
            >
              <summary>Podgląd pliku</summary>
              <pre>{f.xml}</pre>
            </Box>
          </Box>
        ))}
      </Stack>
    </SectionCard>
  );
};

const DuplicatesSection = ({ w }: { w: Window }) => {
  const d = w.write?.duplicates;
  if (d === undefined) return null;
  return (
    <SectionCard title="Podobne zlecenia (duplikat)">
      <Box data-testid="control-duplicates">
        {d === null ? (
          <Typography variant="body2" color="text.secondary">
            Kontrola duplikatu nie dotyczy tego zlecenia (wyłączona albo zlecenie jest już w VIA).
          </Typography>
        ) : !d.checked ? (
          <Alert severity="info">{d.uncheckedReason ?? 'Nie sprawdzono'}</Alert>
        ) : d.candidates.length === 0 ? (
          <Alert severity="success">Brak podobnych zleceń w banku.</Alert>
        ) : (
          <Alert severity={d.unconfirmed > 0 ? 'warning' : 'info'}>
            Podobne zlecenia: {d.candidates.length}
            {d.unconfirmed > 0 ? `, niepotwierdzone: ${d.unconfirmed}` : ''}.{' '}
            {d.writeRefusal ?? 'Potwierdzone „to nie duplikat”.'} Potwierdzenie — w panelu zapisu niżej.
          </Alert>
        )}
      </Box>
    </SectionCard>
  );
};

const StateSection = ({ w }: { w: Window }) => {
  // „Zapisane w TMS” — paczka p. Mariusza 21.10 (BR-ADM-10), `tmsExportOneStep`.
  const tmsNames = useFeatureState(FEATURE_TMS_EXPORT_ONE_STEP) === true;
  return (
  <SectionCard title="Stan zapisu do VIA">
    <Stack spacing={1} data-testid="control-state" data-stage={w.stage?.code ?? ''}>
      {w.stage ? (
        <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Chip size="small" color={w.stage.failed ? 'error' : 'default'} label={w.stage.label} data-testid="control-state-chip" />
          <Typography variant="caption" color="text.secondary">
            {w.stage.atLabel}
            {w.stage.at ? `: ${dateTimeNoSeconds(w.stage.at)}` : ''}
          </Typography>
        </Stack>
      ) : null}
      {w.stage ? <Typography variant="body2">{w.stage.detail}</Typography> : null}
      {w.order?.tmsConfirmedNumber ? (
        <Alert severity="success" data-testid="control-in-tms">
          {tmsNames ? 'Zapisane w TMS' : 'Jest w TMS'} jako {w.order.tmsConfirmedNumber}
          {w.order.tmsConfirmedAt ? ` (${dateTimeNoSeconds(w.order.tmsConfirmedAt)})` : ''}.
        </Alert>
      ) : null}
      <Typography variant="caption" color="text.secondary" data-testid="control-result-note">
        {w.resultNote}
      </Typography>
    </Stack>
  </SectionCard>
  );
};

const ControlScreen = ({ id }: { id: string }) => {
  const createPath = useCreatePath();
  const isAdministration = useIntakeSide('administration');
  const [w, setW] = useState<Window | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { flow, error: flowError, reload: reloadFlow } = useIntakeFlow(id);
  // Pola obowiązkowe (24.10.2026, `requiredFieldsHighlight`) i pytanie
  // Administracji przy polu (`intakeQuestionField`).
  const isRequired = useRequiredFields();
  // Przy otwartym źródle sekcje okna w jednej kolumnie (źródło zajmuje lewą).
  const sourceOpen = useSourcePreview()?.open ?? false;
  const label = (text: string, keys: readonly string[]) => (
    <>
      {fieldLabel(text, keys.some((k) => isRequired(k)))}
      <QuestionMarker question={flow?.openQuestion ?? null} keys={keys} />
    </>
  );

  // „pokaż w źródle” przy polu bez wartości (29.10.2026, `sourcePreview`).
  const srcLink = (keys: readonly string[]) => {
    if (!w) return null;
    const l = sourceLinkFor(w.preOrder as unknown as Record<string, unknown>, keys);
    return l.show ? <ShowInSourceLink hint={l.hint} field={keys[0]} /> : null;
  };

  const load = useCallback(async () => {
    try {
      setW(await fetchControlWindow(id));
      setError(null);
    } catch (e) {
      setError(toErrorMessage(e));
    }
  }, [id]);

  // Inne prezlecenie (nawigacja między oknami) — bez starych danych na ekranie,
  // dopóki nie przyjdą nowe.
  useEffect(() => {
    setW(null);
    void load();
  }, [load]);

  const reloadAll = () => {
    void load();
    void reloadFlow();
  };

  return (
    <Box sx={{ p: 1 }} data-testid="intake-control-window" data-pre-order-id={w?.preOrder.id ?? ''}>
      <Title title={`Okno kontroli — prezlecenie #${id}`} />
      {error ? <Alert severity="error">{error}</Alert> : null}
      {!w && !error ? <CircularProgress size={24} /> : null}
      <Box sx={{ mb: 1 }}>
        <SourcePreviewToggle />
      </Box>
      {w ? (
        <SourceSplit contractPrefix="adm">
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: sourceOpen ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) minmax(0, 1fr)' },
            gap: 2,
            alignItems: 'start',
          }}
        >
          <Stack spacing={2} sx={{ minWidth: 0 }}>
            <SectionCard title="Prezlecenie">
              <Table size="small">
                <TableBody>
                  <Row label={label('Klient', ['clientName'])}>
                    {w.preOrder.clientName ?? '—'}
                    {srcLink(['clientName'])}
                  </Row>
                  <Row label={label('Nr zamówienia klienta', ['customerOrderNumber'])}>
                    {w.preOrder.customerOrderNumber ?? '—'}
                    {srcLink(['customerOrderNumber'])}
                  </Row>
                  <Row label={label('Załadunek', ['loadingCity', 'loadingDate'])}>
                    {[w.preOrder.loadingCountry, w.preOrder.loadingCity].filter(Boolean).join(' ') || '—'} ·{' '}
                    {wallClockDateTime(w.preOrder.loadingDate)}
                    {srcLink(['loadingCity', 'loadingDate'])}
                  </Row>
                  <Row label={label('Rozładunek', ['unloadingCity', 'unloadingDate'])}>
                    {[w.preOrder.unloadingCountry, w.preOrder.unloadingCity].filter(Boolean).join(' ') || '—'} ·{' '}
                    {wallClockDateTime(w.preOrder.unloadingDate)}
                    {srcLink(['unloadingCity', 'unloadingDate'])}
                  </Row>
                  <Row label="Wymagany typ pojazdu">{w.preOrder.requiredVehicleType ?? '—'}</Row>
                  <Row label={label('Spółka', ['companyId'])}>{w.preOrder.company?.name ?? '—'}</Row>
                  <Row label="Zlecenie">
                    {w.order ? `${w.order.orderNumber ?? `#${w.order.id}`} · ${w.order.stateLabel}` : 'jeszcze nie ma'}
                  </Row>
                  {w.copy.from ? (
                    <Row label="Kopia">
                      <span data-testid="control-copy">
                        skopiowane z{' '}
                        <Link to={createPath({ resource: 'pre-orders', type: 'show', id: w.copy.from.id })}>
                          #{w.copy.from.id}
                        </Link>
                        {w.copy.confirmedKept ? ` · potwierdzone pola: ${w.copy.confirmedKept}` : ''}
                      </span>
                    </Row>
                  ) : null}
                </TableBody>
              </Table>
              <Stack direction="row" spacing={1} useFlexGap sx={{ mt: 1, flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  startIcon={<OpenInNewIcon />}
                  component={Link}
                  to={createPath({ resource: 'pre-orders', type: 'show', id: w.preOrder.id })}
                >
                  Karta prezlecenia
                </Button>
                {w.order ? (
                  <Button
                    size="small"
                    startIcon={<OpenInNewIcon />}
                    component={Link}
                    to={createPath({ resource: 'orders', type: 'show', id: w.order.id })}
                  >
                    Zlecenie i dane dla importera
                  </Button>
                ) : null}
                <Button size="small" startIcon={<RefreshIcon />} onClick={reloadAll}>
                  Odśwież
                </Button>
              </Stack>
            </SectionCard>
            {flow || flowError ? (
              <IntakeFlowCard flow={flow} error={flowError} onChanged={reloadAll} />
            ) : null}
            <BlockersSection w={w} />
            {w.contractor ? (
              <PreOrderContractorSection
                preOrderId={w.preOrder.id}
                clientName={w.preOrder.clientName}
                view={w.contractor}
                showVat={false}
                lockedReason={
                  flow && !flow.mySides.includes(flow.holderSide)
                    ? `Prezlecenie jest u działu ${flow.department} — zmiany wprowadza ${flow.department}.`
                    : null
                }
                onChanged={reloadAll}
              />
            ) : null}
            <VatSection vat={w.vat} contractor={w.contractor} withDecisions={!!w.decisions} />
            <FuelSurchargeSection kind="pre-order" id={w.preOrder.id} refreshKey={w} />
            <DuplicatesSection w={w} />
          </Stack>
          <Stack spacing={2} sx={{ minWidth: 0 }}>
            {/* Lista „Do zrobienia” — decyzje Administracji przed eksportem (26.10.2026, S-26.10-06/-07). */}
            {w.decisions ? (
              <IntakeDecisionsPanel
                preOrderId={w.preOrder.id}
                decisions={w.decisions}
                canAct={isAdministration}
                returnToCs={flow && isAdministration ? flow.returnToCs : null}
                onChanged={reloadAll}
              />
            ) : null}
            <StateSection w={w} />
            {/* Pliki od klienta (26.10.2026, `preOrderAttachments`, S-26.10-13) — to, z czego wpisano prezlecenie. */}
            <PreOrderAttachments preOrderId={w.preOrder.id} />
            <FilesSection w={w} />
            {w.order && isAdministration ? (
              <RecordContextProvider
                value={{
                  id: w.order.id,
                  viaWriteStatus: w.order.viaWriteStatus,
                  tmsConfirmedNumber: w.order.tmsConfirmedNumber,
                  tmsConfirmedAt: w.order.tmsConfirmedAt,
                }}
              >
                {/* Po eksporcie (także nieudanym) stan zapisu w oknie czyta się od nowa (25.10.2026). */}
                <ViaWritePanel
                  onChanged={reloadAll}
                  exportBlock={
                    w.decisions && w.decisions.pending > 0
                      ? { count: w.decisions.pending, reason: w.decisions.exportRefusal ?? '' }
                      : null
                  }
                />
              </RecordContextProvider>
            ) : null}
            {w.order && !isAdministration ? (
              <Alert severity="info">Zatwierdzenie i plik dla VIA należą do Administracji.</Alert>
            ) : null}
          </Stack>
        </Box>
        </SourceSplit>
      ) : null}
    </Box>
  );
};
